import { API } from "@/lib/api";

async function request(path, options = {}) {
  const res = await fetch(`${API}/hbm-social${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    let detail = "HBM Social request failed.";
    try {
      const body = await res.json();
      detail = body?.detail || detail;
    } catch {
      detail = await res.text().catch(() => detail);
    }
    const message = Array.isArray(detail)
      ? detail.map((x) => x.msg || String(x)).join(" ")
      : (detail?.message || detail?.error || (typeof detail === "string" ? detail : "HBM Social request failed."));
    throw new Error(message);
  }
  return res.json();
}

export const hbmSocialApi = {
  status: () => request("/status"),
  getProviderConfig: () => request("/admin/provider-config"),
  saveProviderConfig: (payload) => request("/admin/provider-config", { method: "POST", body: JSON.stringify(payload) }),
  taxonomy: () => request("/taxonomy"),
  listSituations: (status = "all") => request(`/situations?status=${encodeURIComponent(status)}`),
  getSituation: (id) => request(`/situations/${encodeURIComponent(id)}`),
  analyze: ({ body, title, category, mode }) =>
    request("/private/analyze", { method: "POST", body: JSON.stringify({ body, title, category, mode }) }),
  anonymize: (id) => request(`/situations/${encodeURIComponent(id)}/anonymize`, { method: "POST", body: "{}" }),
  publish: (id) => request(`/situations/${encodeURIComponent(id)}/publish`, { method: "POST", body: "{}" }),
  addPerspective: (id, payload) =>
    request(`/situations/${encodeURIComponent(id)}/perspectives`, { method: "POST", body: JSON.stringify(payload) }),
  continueSituation: (id, payload) =>
    request(`/situations/${encodeURIComponent(id)}/turns`, { method: "POST", body: JSON.stringify(payload) }),
  forceSynthesize: (id) => request(`/situations/${encodeURIComponent(id)}/force-synthesize`, { method: "POST", body: "{}" }),
  closeSituation: (id) => request(`/situations/${encodeURIComponent(id)}/close`, { method: "POST", body: "{}" }),
  synthesize: (id) => request(`/situations/${encodeURIComponent(id)}/synthesize`, { method: "POST", body: "{}" }),
  reportOutcome: (id, payload) =>
    request(`/situations/${encodeURIComponent(id)}/outcome`, { method: "POST", body: JSON.stringify(payload) }),
  listResponseTraces: (limit = 25, filters = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
    });
    return request(`/response-traces?${params.toString()}`);
  },
  getResponseTrace: (requestId) => request(`/response-traces/${encodeURIComponent(requestId)}`),
  liveStatus: () => request("/live/status"),
  analyticsDashboard: () => request("/analytics/dashboard"),
  calibrationDashboard: () => request("/analytics/calibration"),
  moderationDashboard: () => request("/analytics/moderation"),
  getProfile: () => request("/profile"),
  updateProfile: (payload) => request("/profile", { method: "PUT", body: JSON.stringify(payload) }),
  listProfiles: (q = "") => request(`/profiles${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  socialAction: (payload) => request("/intelligence", { method: "POST", body: JSON.stringify(payload) }),
  listCommunities: () => request("/communities"),
  createCommunity: (payload) => request("/communities", { method: "POST", body: JSON.stringify(payload) }),
  joinCommunity: (id) => request(`/communities/${encodeURIComponent(id)}/join`, { method: "POST", body: "{}" }),
  createCommunityInvite: (id) => request(`/communities/${encodeURIComponent(id)}/invite`, { method: "POST", body: "{}" }),
  listMessages: () => request("/messages"),
  sendMessage: (payload) => request("/messages", { method: "POST", body: JSON.stringify(payload) }),
  getMessageThread: (id) => request(`/messages/${encodeURIComponent(id)}`),
  createReport: (payload) => request("/reports", { method: "POST", body: JSON.stringify(payload) }),
  listReports: (status = "open") => request(`/admin/reports?status=${encodeURIComponent(status)}`),
  resolveReport: (id, payload) => request(`/admin/reports/${encodeURIComponent(id)}`, { method: "POST", body: JSON.stringify(payload) }),
  listForecasts: () => request("/forecasts"),
  createForecast: (payload) => request("/forecasts", { method: "POST", body: JSON.stringify(payload) }),
  getForecast: (id) => request(`/forecasts/${encodeURIComponent(id)}`),
  contributeForecast: (id, payload) => request(`/forecasts/${encodeURIComponent(id)}/entries`, { method: "POST", body: JSON.stringify(payload) }),
  resolveForecast: (id, payload) => request(`/forecasts/${encodeURIComponent(id)}/resolve`, { method: "POST", body: JSON.stringify(payload) }),
  calibration: () => request("/calibration"),
  listRealityLedger: () => request("/reality-ledger"),
  addLedgerReflection: (id, payload) => request(`/reality-ledger/${encodeURIComponent(id)}/reflection`, { method: "POST", body: JSON.stringify(payload) }),
  socialSearch: (q) => request(`/search?q=${encodeURIComponent(q || "")}`),
  notifications: () => request("/notifications"),
};